import type { ReactNode } from 'react';
import { BiDotsVerticalRounded } from 'react-icons/bi';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { IoClose } from 'react-icons/io5';

import { Modal } from '@/components';
import { ActionsButton, type ActionMenuItem } from '@/components/ActionsButton';
import SidePanel from '@/components/SidePanel/SidePanel';

import styles from './ModalOrPanel.module.scss';

type ModalOrPanelProps = {
  asPanel?: boolean;
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  /** Panel only — the modal shell has no room under its centred title. */
  subtitle?: string;
  badge?: ReactNode;
  /** Panel only — the modal shell owns its own header row. */
  headerActions?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  haveActions?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
};

export const ModalOrPanel = ({
  asPanel,
  open,
  onClose,
  title,
  subtitle,
  badge,
  headerActions,
  footer,
  children,
  haveActions,
  onEdit,
  onDelete,
}: ModalOrPanelProps) => {
  if (!asPanel) {
    // The modal shell has no header or footer regions of its own, so the badge and the
    // footer ride in with the content: without them a transaction awaiting approval would
    // open with nothing to approve or reject it by.
    return (
      <Modal
        open={open}
        onClose={onClose}
        title={title}
        haveActions={haveActions}
        onEdit={onEdit}
        onDelete={onDelete}
      >
        {badge && <div className={styles.modalBadge}>{badge}</div>}
        {children}
        {footer && <div className={styles.modalFooter}>{footer}</div>}
      </Modal>
    );
  }

  const actionItems: ActionMenuItem[] = [];
  if (onEdit) {
    actionItems.push({ label: 'Edit', icon: <FiEdit2 size={14} />, onClick: onEdit });
  }
  if (onDelete) {
    actionItems.push({
      label: 'Delete',
      icon: <FiTrash2 size={14} />,
      variant: 'danger',
      onClick: onDelete,
    });
  }

  return (
    <SidePanel open={open} onClose={onClose}>
      <div className={styles.root}>
        <header className={styles.header}>
          <button type="button" className={styles.close} aria-label="Close" onClick={onClose}>
            <IoClose />
          </button>
          <div className={styles.identity}>
            <div className={styles.titleRow}>
              <h2 className="headline-small">{title}</h2>
              {badge}
            </div>
            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          </div>
          <div className={styles.headerRight}>
            {headerActions && <div className={styles.headerActions}>{headerActions}</div>}
            {haveActions && actionItems.length > 0 && (
              <ActionsButton
                items={actionItems}
                ariaLabel="Actions"
                icon={<BiDotsVerticalRounded />}
              />
            )}
          </div>
        </header>
        <div className={styles.body}>{children}</div>
        {footer && <footer className={styles.footer}>{footer}</footer>}
      </div>
    </SidePanel>
  );
};

export default ModalOrPanel;
